import React from 'react';

/**
 * One shape of a loading screen: a softly shimmering block that stands exactly where the real
 * content will appear, so the page does not jump when it arrives. Size and radius come from the
 * caller's classes; the block itself carries no meaning and is hidden from assistive technology.
 */
export function SkeletonBlock({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`block rounded-md bg-skeleton-sheen bg-[length:200%_100%] motion-safe:animate-bp-sheen ${className || 'h-4 w-full'}`}
    />
  );
}

/** What a name still being read is called: the word beside its loading block, and the words of a list showing it. */
export const NAME_LOADING_COPY = 'Loading';

/**
 * A name still being read, standing inline where the name will appear: a short block, and the word
 * for assistive technology. The key or id the name is read by never stands in its place.
 */
export function NameLoading() {
  return (
    <>
      <SkeletonBlock className="inline-block h-2.5 w-14 align-middle" />
      <span className="sr-only">{NAME_LOADING_COPY}</span>
    </>
  );
}

/** A trend chart's placeholder inside its section card while the chart's first answer is on the way. */
export function TrendChartSkeleton() {
  return (
    <div className="flex h-[348px] items-end gap-6 px-14 pb-12" aria-hidden="true">
      {['h-[45%]', 'h-[55%]', 'h-[50%]', 'h-[65%]', 'h-[70%]', 'h-[80%]'].map((height, index) => (
        <SkeletonBlock key={index} className={`w-full rounded-t-md rounded-b-none ${height}`} />
      ))}
    </div>
  );
}

/** Rows of a table, a name and three figures each, while the table's first answer is on the way. */
export function TableRowsSkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="grid grid-cols-[1fr_6rem_6rem_6rem] items-center gap-6">
          <SkeletonBlock className={`h-4 ${row % 2 ? 'w-1/2' : 'w-2/3'}`} />
          <SkeletonBlock className="h-4 w-12" />
          <SkeletonBlock className="h-4 w-12" />
          <SkeletonBlock className="h-4 w-12" />
        </div>
      ))}
    </div>
  );
}

/**
 * Wraps the shapes of a loading screen and tells assistive technology what is loading, once,
 * instead of letting it read out a column of empty blocks.
 */
export function SkeletonRegion({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/**
 * A thin bar under the navigation that says "working on it" while the screen already shows content:
 * a refresh, another page of a list, a new sort. Nothing moves while it runs.
 */
export function IndeterminateProgress({ active, label }: { active: boolean; label: string }) {
  return (
    <div
      className={`pointer-events-none relative h-0.5 w-full overflow-hidden transition-opacity duration-300 ${active ? 'opacity-100' : 'opacity-0'}`}
      role={active ? 'progressbar' : undefined}
      aria-label={active ? label : undefined}
      aria-hidden={active ? undefined : true}
    >
      <span className="absolute inset-y-0 left-0 w-2/5 rounded-full bg-brand-gradient motion-safe:animate-bp-indeterminate" />
    </div>
  );
}
